import { useEffect } from 'react';
import { refreshAccessToken } from '@/networks/interceptors';
import { useAuthStore } from '@/stores/useAuthStore';
import { tokenExpiry } from '@/utils/tokenExpiry';

/** 만료 이 시간 전에 미리 갱신한다. */
const REFRESH_AHEAD = 60 * 1000;

/**
 * 일반 앱처럼 화면을 열어 둔 동안 회원 로그인을 유지한다. 접근 토큰이 만료되기 전에 갱신하고,
 * 다른 탭에서 돌아오거나 다시 연결될 때 이미 만료가 가까우면 즉시 갱신한다.
 * 갱신 토큰이 거절되면 기존 갱신 로직이 로그아웃한다.
 */
export function useTokenKeepAlive() {
  const accessToken = useAuthStore((s) => s.accessToken);
  useEffect(() => {
    const expiresAt = tokenExpiry(accessToken);
    if (!expiresAt) return;
    const refresh = () => {
      if (
        document.visibilityState === 'hidden' ||
        navigator.onLine === false ||
        tokenExpiry(useAuthStore.getState().accessToken) - Date.now() >
          REFRESH_AHEAD
      )
        return;
      void refreshAccessToken().catch(() => undefined);
    };
    const timer = setTimeout(
      refresh,
      Math.max(0, expiresAt - Date.now() - REFRESH_AHEAD),
    );
    const visible = () => {
      if (document.visibilityState !== 'hidden') refresh();
    };
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('online', refresh);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('online', refresh);
    };
  }, [accessToken]);
}
